<template>
  <LearnLayout :style="theme.style" class="relative h-full vld-parent" :class="'play-' + playId">
    <Loading
      :active.sync="isLoading"
      :is-full-page="true"
      :opacity="1"
      blur=""
      class="text-lg font-semibold leading-loose text-center shadow-none sm:text-xl"
    >
      <div class="container mx-auto text-black">
        잠시만 기다려 주세요.
      </div>
    </Loading>
    <!--end::Loading-->

    <BaseEventObserver :elements="['button']" :events="['click', 'answer']">
      <swiper
        ref="vSwiper"
        :auto-destroy="true"
        :auto-update="true"
        :cleanup-styles-on-destroy="true"
        :delete-instance-on-destroy="true"
        class="h-full swiper"
        @ready="ready"
        @slideChangeTransitionStart="sceneChange"
      >
        <swiper-slide v-for="(scene, index) of scenes" :key="index" class="h-full">
          <SceneInner :id="'bs-' + scene.id" :extend="sceneExtend">
            <component
              :is="scene.component"
              :id="scene.id"
              :ref="'scene' + index"
              :class="'scene-' + scene.id"
              :next="next"
            />
            <!--end::scene-->
          </SceneInner>
          <!--end::base-scene-->
        </swiper-slide>
        <!--end::slide-->
      </swiper>
      <!--end::Swiper-->
    </BaseEventObserver>
    <!--end::EventObserver-->

    <div class="relative">
      <SceneOuter :extend="globalExtend" :scenes="scenes" />
    </div>
  </LearnLayout>
  <!--end::learn-layout-->
</template>

<script>
import * as _ from 'lodash'
import { mapGetters } from 'vuex'
import LearnLayout from '@layouts/learn-layout.vue'
import JGProcessingModal from '@src/games/jongrogu/components/jg-processing.vue'
import JGAlert from '@src/games/jongrogu/components/jg-alert.vue'
import { PLAYS } from '@state/modules/play'
import { EVENT, MODE, PLAY_STATUS } from '@games/constant'
import { EVENT_OBSERVERS } from '@state/modules/event-observer'
import { GAMES } from '@state/modules/game'
import { findGame, PlayGame } from '@src/games/index'
import { LAYOUT } from '@src/games/constant'
import SceneOuter from '@components/game/scene-outer.vue'
import SceneInner from '@components/game/scene-inner.vue'

export default {
  components: {
    SceneInner,
    SceneOuter,
    JGAlert,
    JGProcessingModal,
    LearnLayout,
  },
  props: {
    game: {
      type: String,
      required: true,
    },
    gameId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      startAt: new Date().getMilliseconds(),
      isLoading: true,
      playId: null,
      playGame: null,
      scenes: [],
      sceneExtend: [],
      globalExtend: [],
      theme: {
        style: null,
      },
    }
  },
  computed: {
    ...mapGetters(PLAYS.NAME, [PLAYS.GET_PLAY]),
    ...mapGetters(GAMES.NAME, [GAMES.GET_GAME, GAMES.GET_GAME_DATA, GAMES.GET_GAME_MODES]),
    swiper() {
      return _.get(this.$refs, 'vSwiper.$swiper')
    },
    playMode() {
      return this.$route.query.mode || MODE.BEGINNER
    },
  },
  // initialize
  beforeMount() {
    try {
      this.playGame = new PlayGame(this.game, this.gameId, this.playMode)
      const game = this.playGame.getGame() || {}

      this.$log.debug('initialize game scenes')
      this.playId = this.playGame.getId()
      this.theme = this.playGame.getTheme() || {}

      // if exists last scene code.
      const allGamesSettings = findGame(this.game).games || []
      const gameSetting = allGamesSettings.find((it) => it.id === Number(this.gameId))
      this.lastSceneId = _.get(gameSetting, `modes.[${this.playMode}].lastScene`)
      let scenes = game.scenes || []

      if (this.lastSceneId) {
        this.$log.warn('지정된 마지막 scene 이 존재하여 scene 길이가 조정됩니다.', this.lastSceneId)
        const lastSceneIndex = _.findIndex(game.scenes, (v) => v.id === this.lastSceneId)

        if (game.scenes.length < lastSceneIndex) {
          this.$log.error('게임 모드의 지정된 Scene 길이보다 전달된 게임 Scene 이 더 많습니다.')
        }

        scenes = game.scenes.slice(0, lastSceneIndex)
        this.$log.debug('total scene length : ', scenes.length)
      }

      this.scenes = scenes
      this.sceneExtend = game.extend.filter((v) => v.global !== true) || []
      this.globalExtend = game.extend.filter((v) => v.global === true) || []

      this.$log.debug('play game id : ', this.playGame.getId())
      this.$log.debug('initialized play configurations => ', {
        play: this.playGame.getData(),
        game: this.playGame.getGame(),
      })

      this.$store.commit(PLAYS.NAMESPACE(PLAYS.SET_PLAY), this.playGame.getData())
      this.$store.commit(GAMES.NAMESPACE(GAMES.SET_GAME), this.playGame.getGame())

      this.$log.debug('listen on event')
      this.$events.on(EVENT.OBSERVER_CAPTURE_INSIDE, (v) => this.capture(v))
      this.$events.on(EVENT.PLAY_SCENE_MOVE, (v) => this.move(v))
      this.$events.on(EVENT.PLAY_SCENE_NEXT, (v) => this.next(v))
      this.$events.on(EVENT.PLAY_SCENE_PREV, (v) => this.prev(v))
    } catch (e) {
      this.$log.error(e)
      alert('게임을 초기화 할 수 없습니다. 홈으로 이동합니다.')
      window.location.href = '/home'
    }
  },
  // un-initialize
  beforeDestroy() {
    this.$log.debug('off event listener')

    // clear event
    this.$events.off(EVENT.OBSERVER_CAPTURE_INSIDE)
    this.$events.off(EVENT.PLAY_SCENE_MOVE)
    this.$events.off(EVENT.PLAY_SCENE_NEXT)

    // clear state
    this.$store.dispatch(PLAYS.NAMESPACE(PLAYS.CLEAR))
    this.$store.dispatch(EVENT_OBSERVERS.NAMESPACE(EVENT_OBSERVERS.CLEAR))
  },
  methods: {
    ready(swiper) {
      this.$log.debug('swiper ready!!')

      // for development
      if (process.env.NODE_ENV !== 'production') {
        const index = _.get(this.$route, 'query.index')
        if (index) {
          this.$log.debug('view debug mode, go to slide -> ', index)
          this.isLoading = false
          this.$store.commit(PLAYS.NAMESPACE(PLAYS.SET_PLAY_STATUS), PLAY_STATUS.START)
          return this.move(index)
        }
      }

      // min delay time for game start
      const minDelay = _.get(this.GET_PLAY_CONFIG, 'minDelay', 3000)
      const gap = new Date().getMilliseconds() - this.startAt
      const delay = gap < minDelay ? minDelay - gap : 0
      this.$log.debug(`min delay [${minDelay}], gap [${gap}], result [${delay}]`)

      setTimeout(() => {
        this.isLoading = false
        this.$events.emit(EVENT.PLAY_GAME_READY)
        this.sceneChange(swiper)

        if (window.innerHeight > LAYOUT.BASE_VERTICAL_HEIGHT) {
          this.matchSceneHeight()
        }
      }, delay)
    },
    getActiveSceneIndex: function() {
      return _.get(this.swiper, 'realIndex', 0)
    },
    // capture the user touch event
    capture({ event }) {
      if (!event.answer && event.type !== 'answer') {
        // if fail, open the modal
        return this.$modal.show('mission-fail')
      }
    },
    prev() {
      // move to previous scene
      this.move(this.getActiveSceneIndex() - 1)
    },
    next(some) {
      // get scene id
      const value = some ? some.value : null
      const sceneId = some ? some.sceneId : null
      const id = _.get(this.scenes, `[${this.getActiveSceneIndex()}].id`)
      const required = _.find(_.get(this.GET_GAME_DATA, 'select', []), (it) => it.sceneId === id)
      this.$log.warn(`call next slide from the ${id} scene`)

      if (required && !required.validate(value)) {
        this.$log.error('failed user select validation : ', required.id, value)
      }

      // store receipt item
      this.$store.commit(PLAYS.NAMESPACE(PLAYS.ADD_USER_SELECT), { id, value })

      // next scene index
      const nextIndex = sceneId ? this.scenes.findIndex((it) => it.id === sceneId) : this.getActiveSceneIndex() + 1

      // if next scene is last scene then open the success modal
      if (nextIndex > this.scenes.length - 1) {
        return this.$modal.show('mission-success')
      }

      // move to next scene
      this.move(nextIndex)
    },
    move(sceneIndex) {
      this.$log.debug('move the scene using the index ', sceneIndex)

      try {
        this.swiper.slideTo(sceneIndex)
      } catch (e) {
        this.$log.error('move slide error, swiper not found.', e)
        window.location.reload()
      }
    },
    sceneChange(swiper, retry) {
      retry = !retry ? 3 : retry
      const index = swiper.realIndex
      const length = swiper.slides.length
      let activeSceneId = ''
      let sceneId = ''

      for (let i = 0; i < length; i++) {
        const scene = _.get(this.$refs, `scene${i}[0]`)
        sceneId = _.get(this.scenes, `[${i}].id`, i)
        // const scene = _.get(this.$refs, `vSwiper.$children[${i}]`)

        if (!scene) {
          if (retry > 0) {
            this.$log.debug('retry transition start at ', retry)
            this.$nextTick(() => {
              this.sceneChange(swiper, retry--)
            })
            return
          }

          this.$log.warn('can not find scene ref by index. ', i, sceneId)
          return
        }

        if (i === index) {
          activeSceneId = sceneId
          this.$log.debug('active scene id : ', activeSceneId)
          this.$store.commit(PLAYS.NAMESPACE(PLAYS.SET_ACTIVE_SCENE_INDEX), index)
          this.$store.commit(PLAYS.NAMESPACE(PLAYS.SET_ACTIVE_SCENE_ID), sceneId)
          this.$events.emit(EVENT.PLAY_SCENE_ACTIVE, { index, scene, id: sceneId })

          // activation
          if (_.has(scene, 'activated')) {
            this.$log.debug('scene event on activated : ' + sceneId)
            scene.activated({ index, swiper, id: sceneId })
          }
        } else {
          // inactivation
          if (_.has(scene, 'deactivated')) {
            this.$log.debug('scene event on deactivated : ' + sceneId)
            scene.deactivated({ index, swiper, id: sceneId })
          }
        }
      }

      const percent = Math.abs(Math.ceil(swiper.progress * 100))
      this.$events.emit(EVENT.PLAY_CHANGE_SCENE, { index })
      this.$store.commit(PLAYS.NAMESPACE(PLAYS.SET_PLAY_PERCENT), percent)

      this.$log.debug(
        'current scene reference index : ',
        index,
        ' activeSceneId : ',
        activeSceneId,
        ' percent : ',
        percent
      )
    },
    matchSceneHeight() {
      const elements = document.querySelectorAll('.match-scene-height')
      const gtHeight = _(elements)
        .map((v) => v.clientHeight)
        .sortBy()
        .last()

      elements.forEach((v, k) => (v.style.height = gtHeight + 'px'))
      this.$log.debug('biggest scene height is : ', gtHeight)
    },
  },
}
</script>
